import { calmTokens as t } from '../../shared/tokens';
import { fmtEUR } from '../../shared/format';
import CalmToggle from './CalmToggle';
import {
  IconArrowDown,
  IconArrowUp,
  IconBank,
  IconCheck,
  IconEdit,
  IconSparkle,
  IconTrash,
} from '../../shared/icons';
import { LABEL_STATUS_KINDS, isBankSourced, needsReview } from '../../shared/labels';

function ActionBtn({ icon, title, onClick, hoverColor }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
        if (hoverColor) e.currentTarget.style.color = hoverColor;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = t.textMute;
      }}
      style={{
        width: 30,
        height: 30,
        background: 'transparent',
        border: 'none',
        color: t.textMute,
        borderRadius: 8,
        display: 'grid',
        placeItems: 'center',
        transition: 'all .15s',
        padding: 0,
      }}
    >
      {icon}
    </button>
  );
}

export default function CalmTxCard({ tx, onToggle, onEdit, onDelete }) {
  const isDebit = tx.type === 'debit';
  const isBank = Boolean(tx.withdrawnFromBank);
  const isPerso = Boolean(tx.withdrawnFromPersonal);
  const bothFlags = isBank && isPerso;
  const fromBank = isBankSourced(tx);
  const review = needsReview(tx);
  const statusKind = LABEL_STATUS_KINDS[tx.labelStatus] || LABEL_STATUS_KINDS.unmatched;

  return (
    <div
      className="calm-tx-row"
      style={{
        display: 'grid',
        gridTemplateColumns: '36px 1fr auto auto auto',
        alignItems: 'center',
        gap: 14,
        padding: 'var(--tx-pad)',
        borderRadius: 14,
        transition: 'background .15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: isDebit ? t.debitDim : t.creditDim,
          border: `1px solid ${isDebit ? t.debitBorder : t.creditBorder}`,
          display: 'grid',
          placeItems: 'center',
          color: isDebit ? t.debit : t.credit,
        }}
        aria-hidden="true"
      >
        {isDebit ? <IconArrowUp size={15} /> : <IconArrowDown size={15} />}
      </div>

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            color: bothFlags ? t.warn : t.text,
            fontWeight: 500,
            letterSpacing: -0.15,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontFamily: t.fontUi,
          }}
          title={tx.description}
        >
          {tx.description}
        </div>
        {review && (
          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            {fromBank && (
              <span
                title={tx.bankRawLabel ? `Banque : ${tx.bankRawLabel}` : 'Importée depuis la banque'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 500,
                  color: t.accentSoft,
                  background: 'rgba(165,180,252,0.12)',
                  border: '1px solid rgba(165,180,252,0.28)',
                  maxWidth: 220,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <IconBank size={10} />
                {tx.bankLabel || 'Banque'}
              </span>
            )}
            {review && (
              <span
                title="Libellé proposé automatiquement : à vérifier"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 500,
                  color: statusKind.color,
                  background: statusKind.bg,
                  border: `1px solid ${statusKind.border}`,
                  whiteSpace: 'nowrap',
                }}
              >
                <IconSparkle size={10} />
                À vérifier
              </span>
            )}
          </div>
        )}
      </div>

      <div
        style={{
          fontFamily: t.fontMono,
          fontSize: 15,
          fontWeight: 500,
          color: isDebit ? t.debit : t.credit,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: -0.3,
          textAlign: 'right',
          whiteSpace: 'nowrap',
          paddingRight: 6,
        }}
      >
        {isDebit ? '− ' : '+ '}
        {fmtEUR(tx.amount)}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 6,
          padding: 'var(--tx-toggle-pad, 4px)',
          background: 'rgba(0,0,0,0.2)',
          border: `1px solid ${t.border}`,
          borderRadius: 14,
        }}
      >
        <CalmToggle
          active={isBank}
          color="bank"
          icon={<IconBank size={15} />}
          title={isBank ? 'Retirée du compte bancaire' : 'Marquer comme retirée du compte'}
          onClick={() => onToggle && onToggle(tx._id, 'withdrawnFromBank')}
        />
        <CalmToggle
          active={isPerso}
          color="perso"
          icon={<IconCheck size={15} />}
          title={isPerso ? 'Comptée dans le solde perso' : 'Compter dans le solde perso'}
          onClick={() => onToggle && onToggle(tx._id, 'withdrawnFromPersonal')}
        />
      </div>

      <div style={{ display: 'flex', gap: 2, marginLeft: 4 }}>
        <ActionBtn
          icon={<IconEdit size={14} />}
          title="Modifier"
          onClick={() => onEdit && onEdit(tx)}
          hoverColor={t.accent}
        />
        <ActionBtn
          icon={<IconTrash size={14} />}
          title="Supprimer"
          onClick={() => onDelete && onDelete(tx._id)}
          hoverColor={t.debit}
        />
      </div>
    </div>
  );
}
